import React, { forwardRef } from 'react';

import styles from './kanban-item-card.module.scss';
type KanbanItemCardProps = {
  /** Card title (single line, truncated — same as the step card). */
  title: string;

  /** Leading element, rendered to the left of the content (e.g. status selector). */
  leading?: React.ReactNode;

  /** Trailing element on the title row (e.g. assignee avatar). */
  trailing?: React.ReactNode;

  /** Footer row content (e.g. date + actions). Omitted row when not provided. */
  footer?: React.ReactNode;

  /** Title-cases each word — kept on for steps; off for free-text task titles. */
  capitalizeTitle?: boolean;

  status?: string;
  isDone?: boolean;
  isDragging?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'title' | 'onClick'>;

/**
 * Presentational kanban card shell shared by the project-overview step card
 * (`StepItem`) and the tasks board card (`TaskCard`). Owns the layout and
 * styling only — callers provide the status/assignee/footer content.
 */
export const KanbanItemCard = forwardRef<HTMLDivElement, KanbanItemCardProps>(
  (
    {
      title,
      leading,
      trailing,
      footer,
      capitalizeTitle,
      status,
      isDone,
      isDragging,
      onClick,
      ...rest
    },
    ref,
  ) => (
    <div
      className={styles['container']}
      ref={ref}
      data-status={status}
      data-is-done={isDone}
      data-dragging={isDragging}
      onClick={onClick}
      {...rest}
    >
      {leading}

      <div className={styles['wrapper']}>
        <div className={`${styles['header']} row`}>
          <div className={styles['title']} data-capitalize={capitalizeTitle}>
            {title}
          </div>

          {trailing}
        </div>

        {footer && (
          <div className={`${styles['footer']} row`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  ),
);
